import { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { Button, Input, Divider } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { FailureAlert } from '@vnetwork/margarita-shell/FailureAlert';
import { sessionEnded } from '@vnetwork/margarita-shell/failure-copy';
import { PasswordInput } from './AuthShell';
import styles from './LoginForm.module.css';
import form from './AuthForm.module.css';
import { startSsoLogin, directGrantLogin } from '@/lib/sso';
import { requestGoogleAuthCode } from '@/lib/google-signin';
import { env } from '@/config/env';
import { useAuth } from '@/auth/AuthProvider';
import { brand } from '@/config/brand';
import {
  loginUser,
  resolveVerification,
  checkMigrationable,
  sendMigrationOtp,
  exchangeGoogleCode,
  ERR_VERIFICATION_REQUIRED,
} from '@/api/auth-flow';
import { isAxiosError } from 'axios';
import { resolveAuthError } from '@/api/auth-error';
import { submitWebsiteLead } from '@/api/leads';
import { returnState } from '../return-to';
import { sessionEndedFrom } from '@/lib/session-return';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Google "G" mark (official brand colors) for the social login button. */
function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

/** VNETWORK-ID brand mark for the SSO social button (the icon is already the red squircle). */
function VnetworkIdIcon() {
  return <img className={styles.vnIdIcon} src={brand.assets.icon} alt="" width={22} height={22} />;
}

interface LoginFormProps {
  onEmailMfa: (email: string, uncompleted: boolean) => void;
  onTotpMfa: (email: string, password: string) => void;
}

interface FormValues {
  email: string;
  password: string;
}

export function LoginForm({ onEmailMfa, onTotpMfa }: LoginFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setSession } = useAuth();
  // Where to land after auth: the page that bounced us here (ProtectedRoute
  // sets state.from, incl. query) — e.g. a reconciliation deep link. Defaults
  // to the dashboard. Carried through password, SSO and the migration/onboard flow.
  // A forced logout cannot carry router state across its full page load: the query says why and where from.
  const ended = sessionEndedFrom(searchParams);
  const redirectTo =
    (location.state as { from?: string } | null)?.from || ended.from;
  // A flow that bounced here (e.g. an invite for an already-registered email) can
  // pass an email to prefill and a one-line notice explaining why to sign in.
  const navState = location.state as { email?: string; notice?: string } | null;
  const [error, setError] = useState<string | null>(
    searchParams.get('error') === 'callback_failed'
      ? `SSO login failed: ${searchParams.get('message') ?? 'authentication error'}`
      : null,
  );
  // When the brand disables SSO, the form authenticates via Keycloak Direct
  // Grant and every SSO/Google redirect surface is hidden.
  const ssoEnabled = brand.features.sso;
  const [ssoLoading, setSsoLoading] = useState(false);
  const [directLoading, setDirectLoading] = useState(false);
  // Google sign-in has its own spinner so the VNETWORK-ID button doesn't spin
  // when the user clicked "Continue with Google".
  const [googleLoading, setGoogleLoading] = useState(false);
  // Login is email-first: we resolve the email against the backend before asking
  // for a password, so migrating accounts can be routed to the OTP flow instead.
  const [phase, setPhase] = useState<'email' | 'password'>('email');
  const [checking, setChecking] = useState(false);

  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { email: navState?.email ?? '', password: '' } });

  const { mutateAsync, isPending } = useMutation({ mutationFn: loginUser });

  // Step 1: resolve the email. A migrationable account is sent an OTP and routed
  // to /auth/migration. An SSO-only account (already migrated, local disabled) is
  // sent straight to Keycloak — its local password would be rejected. Everyone
  // else proceeds to the password step. Any backend failure falls through to the
  // password step so login is never blocked.
  const onContinue = async () => {
    setError(null);
    if (!(await trigger('email'))) return;
    const email = getValues('email');
    // SSO disabled → no email-first routing (no migration/SSO redirect); go
    // straight to the password step, which authenticates via Direct Grant.
    if (!ssoEnabled) {
      setPhase('password');
      return;
    }
    setChecking(true);
    try {
      const result = await checkMigrationable(email);
      if (result?.migrationable) {
        await sendMigrationOtp(email);
        navigate('/auth/migration', { state: { email, from: redirectTo } });
        return;
      }
      // SSO account with no usable local password (no local record, or the
      // legacy login is locked because it was already migrated) → go straight
      // to Keycloak. A live local login (has_local && !local_locked) still
      // falls through to the password step.
      if (result?.has_sso && !(result.has_local && !result.local_locked)) {
        await startSsoLogin(redirectTo); // redirects to Keycloak; resolves before unload
        return;
      }
    } catch {
      // Ignore — fall through to the normal password login.
    }
    setChecking(false);
    setPhase('password');
  };

  const onSubmit = async (values: FormValues) => {
    setError(null);

    // SSO disabled → authenticate directly against Keycloak (password grant),
    // no redirect. The session is Keycloak-issued, so it refreshes there.
    if (!ssoEnabled) {
      setDirectLoading(true);
      try {
        const session = await directGrantLogin(values.email, values.password);
        if (setSession(session, 'sso')) {
          navigate(redirectTo, { replace: true });
        } else {
          setError('Invalid response from server. Please try again.');
        }
      } catch (e) {
        // Keycloak returns 401/400 (invalid_grant) for bad username/password.
        if (isAxiosError(e) && (e.response?.status === 401 || e.response?.status === 400)) {
          setError('Invalid email or password.');
        } else {
          setError(resolveAuthError(e, 'Login failed. Please try again.'));
        }
      } finally {
        setDirectLoading(false);
      }
      return;
    }

    try {
      const res = await mutateAsync(values);

      if (res.errors?.length) {
        // Account not yet verified → send a code and switch to email step.
        if (res.errors[0].code === ERR_VERIFICATION_REQUIRED) {
          const verify = await resolveVerification(values.email);
          if (verify.data?.created) {
            onEmailMfa(values.email, true);
            return;
          }
          setError(verify.errors?.[0]?.message ?? res.errors[0].message);
          return;
        }
        setError(res.errors[0].message);
        return;
      }

      const { mfa } = res.data ?? {};
      if (mfa?.enable) {
        if (mfa.method === 'totp') {
          onTotpMfa(values.email, values.password);
          return;
        }
        if (mfa.method === 'email') {
          const verify = await resolveVerification(values.email);
          if (verify.data?.created) {
            onEmailMfa(values.email, false);
            return;
          }
          setError(verify.errors?.[0]?.message ?? 'Could not send verification code.');
          return;
        }
      }

      if (setSession(res.data ?? {})) {
        navigate(redirectTo, { replace: true });
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (e) {
      // A 401 on login means bad credentials — show a human message instead of
      // leaking the raw "Request failed with status code 401".
      if (isAxiosError(e) && e.response?.status === 401) {
        setError('Invalid email or password.');
      } else {
        setError(resolveAuthError(e, 'Login failed. Please try again.'));
      }
    }
  };

  const loginWithSSO = async () => {
    setSsoLoading(true);
    setError(null);
    try {
      await startSsoLogin(redirectTo); // redirects to Keycloak; resolves before unload
    } catch {
      setError('SSO login failed. Please try again.');
      setSsoLoading(false);
    }
  };

  // "Continue with Google". When a standalone Google client is configured
  // (APP_GOOGLE_CLIENT_ID), sign in directly — bypassing Keycloak: pop Google's
  // consent, exchange the returned auth code for app tokens (marked 'google', so
  // the session refreshes via the local auth service, not Keycloak). Otherwise
  // fall back to the Google-via-Keycloak (VNETWORK-ID) flow.
  const loginWithGoogle = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      if (env.google.clientId) {
        const code = await requestGoogleAuthCode(env.google.clientId);
        const session = await exchangeGoogleCode({ code });
        // Same as the sign-up page: /partner/auth/google also signs a new person up, so record
        // the website lead here too. Finish it before establishing the session, which can
        // redirect the auth screen; a lead failure does not block login.
        if (session?.email && session.access_token && session.exp && session.uuid) {
          await submitWebsiteLead({ email: session.email }).catch((error) => {
            console.error('Google login lead submission failed', error);
          });
        }
        if (session && setSession(session, 'google')) {
          navigate(redirectTo, { replace: true });
        } else {
          setError('Google sign-in failed. Please try again.');
          setGoogleLoading(false);
        }
      } else {
        await startSsoLogin(redirectTo, 'google'); // redirects to Keycloak
      }
    } catch (e) {
      setError(resolveAuthError(e, 'Google login failed. Please try again.'));
      setGoogleLoading(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      {ended.expired && !error ? (
        <div className={styles.notice}>
          <FailureAlert copy={sessionEnded()} />
        </div>
      ) : null}
      {navState?.notice && !error && !ended.expired ? (
        <div className={styles.notice}>
          <Alert tone="info" title={navState.notice} />
        </div>
      ) : null}
      {error ? (
        <div className={styles.notice}>
          <Alert tone="danger" title={error} />
        </div>
      ) : null}

      {/* Social sign-in first (mockup): Google + VNETWORK-ID side by side, then a
          divider before the email path. Only when SSO is enabled for the brand. */}
      {ssoEnabled && (
        <>
          <div className={styles.socialRow}>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              loading={googleLoading}
              disabled={ssoLoading}
              onClick={loginWithGoogle}
              startIcon={<GoogleIcon />}
            >
              Google
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              loading={ssoLoading}
              disabled={googleLoading}
              onClick={loginWithSSO}
              startIcon={<VnetworkIdIcon />}
            >
              {brand.ssoName}
            </Button>
          </div>
          <Divider className={styles.divider}>Or</Divider>
        </>
      )}

      <Field
        label="Or continue with your email address"
        required
        className={`${form.field} ${styles.emailField}`}
        error={errors.email?.message}
      >
        {(p) => (
          <Input
            {...p}
            type="email"
            inputSize="lg"
            wrapperClassName={styles.control}
            placeholder="Enter your email"
            autoComplete="email"
            readOnly={phase === 'password'}
            invalid={!!errors.email}
            startSlot={<Mail size={16} />}
            {...register('email', {
              required: 'Email is required.',
              pattern: {
                value: EMAIL_RE,
                message: 'Enter a valid email address, e.g. you@company.com',
              },
            })}
          />
        )}
      </Field>

      {phase === 'password' ? (
        <div className={styles.passwordBlock}>
          <Field label="Password" required className={form.field} error={errors.password?.message}>
            {(p) => (
              <PasswordInput
                {...p}
                inputSize="lg"
                wrapperClassName={styles.control}
                placeholder="••••••••"
                autoComplete="current-password"
                invalid={!!errors.password}
                {...register('password', {
                  required: "Password can't be empty.",
                  minLength: { value: 9, message: 'Password must be 9–64 characters.' },
                  maxLength: { value: 64, message: 'Password must be 9–64 characters.' },
                })}
              />
            )}
          </Field>
          <Link to="/auth/forgot-password" className={styles.forgot}>
            Forgot Password?
          </Link>
        </div>
      ) : null}

      {phase === 'email' ? (
        <Button
          type="button"
          size="lg"
          fullWidth
          loading={checking}
          onClick={onContinue}
          className={styles.submitCta}
        >
          {checking ? 'Checking…' : 'Continue'}
        </Button>
      ) : (
        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={isPending || directLoading}
          className={styles.submitCta}
        >
          Sign in
        </Button>
      )}

      <p className={styles.signupLine}>
        Don't have an account?{' '}
        <Link to="/auth/register" state={returnState(redirectTo)} className={styles.signupLink}>
          Sign up
        </Link>
      </p>
    </form>
  );
}
